import { Controller } from '@hotwired/stimulus'

export default class RebrandingCreditAccountModalController extends Controller {
  static targets = [
    'continueBtn',
    'backBtn',
    'amountInput',
    'amountStepContainer',
    'paymentMethodStepContainer',
    'bankTransferDetailsStepContainer',
    'cardSelectionStepContainer',
    'cardFormStepContainer',
    'cardPaymentMethodComponent',
    'cardPayinContainer',
    'form',
    'modalTitle',
    'modalSubtitle',
    'warningLimitAmount',
    'amountSuggestionsOnMobile',
    'amountSuggestions',
    'amountField',
  ];

  static values = {
    payinUrl: String,
    openBankingUrl: String,
    amountStepTitle: String,
    paymentMethodStepTitle: String,
    bankTransferStepTitle: String,
    bankTransferStepInfo: String,
    maxAmount: Number,
    openBankingWarning: String,
    creditCardAmountMax: String,
    cardSelectionStepTitle: String,
    cardFormStepTitle: String,
  }

  connect() {
    this.stepMap = {
      paymentMethod: this.paymentMethodStepContainerTarget,
      amount: this.amountStepContainerTarget,
      bankTransferDetails: this.bankTransferDetailsStepContainerTarget,
      cardSelection: this.cardSelectionStepContainerTarget,
      cardForm: this.cardFormStepContainerTarget,
    };

    this.reset();
  }

  reset() {
    this.currentStep = 'paymentMethod';
    this.stepHistory = [];
    this.amountInputTarget.value = null;
    this.paymentMethod = null;
    this.resetInputValue();
    this.enableContinueBtn();
    this.showCurrentStep();

    const cardController = this.getCardController();
    if (cardController) cardController.reset();
  }

  navigateTo(stepName) {
    this.stepHistory.push(this.currentStep);
    this.currentStep = stepName;
    this.showCurrentStep();
  }

  enableContinueBtn() {
    if (this.amountInputValue() > 0) {
      this.continueBtnTarget.disabled = false;
    } else {
      this.continueBtnTarget.disabled = true;
    }
  }

  disableSubmit(event) {
    event.preventDefault();
  }

  showCurrentStep() {
    Object.values(this.stepMap).forEach((container) => {
      container.classList.add('d-none');
    });

    this.stepMap[this.currentStep].classList.remove('d-none');
    this.changeTitle();

    if (this.currentStep === 'amount' && this.paymentMethod === 'payment_method_credit_card') {
      this.amountFieldTarget.focus();
    }
  }

  submitForm() {
    const amountValue = this.amountInputValue();
    this.warningLimitAmountTarget.classList.add('d-none');

    if (amountValue <= 0) return null;

    const maxAmount = this.maxAmount();
    if (maxAmount && amountValue > maxAmount) {
      this.warningLimitAmountTarget.classList.remove('d-none');
      return false;
    }

    if (this.paymentMethod === 'payment_method_credit_card') {
      const cardController = this.getCardController();

      cardController.amountValue = amountValue;

      if (cardController.hasRegisteredCards()) {
        this.navigateTo('cardSelection');
      } else {
        this.navigateTo('cardForm');
      }
      return;
    }

    return this.formTarget.submit();
  }

  uncheckPaymentMethods() {
    this.paymentMethodStepContainerTarget.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.checked = false;
    });
  }

  goToPreviousStep() {
    if (this.stepHistory.length === 0) {
      this.reset();
      return $(this.element).modal('hide');
    }

    this.currentStep = this.stepHistory.pop();
    this.showCurrentStep();
  }

  goToNewCard() {
    const cardController = this.getCardController();
    if (cardController) cardController.hideFormError();
    this.navigateTo('cardForm');
  }

  changePaymentMethod(event) {
    this.paymentMethod = event.params.method;

    this.resetInputValue();
    this.enableContinueBtn();

    if (this.paymentMethod === 'payment_method_bank_transfer') {
      this.navigateTo('bankTransferDetails');
      return;
    }

    if (this.paymentMethod === 'payment_method_open_banking') {
      this.formTarget.action = this.openBankingUrlValue;
    }

    [...this.amountSuggestionsTargets, ...this.amountSuggestionsOnMobileTargets].forEach((element) => {
      const paymentMethods = JSON.parse(element.dataset.paymentMethods);
      if (paymentMethods.includes(this.paymentMethod)) {
        element.disabled = false;
        element.classList.remove('d-none');
      } else {
        element.disabled = true;
        element.classList.add('d-none');
      }
    });

    this._applyPrefillAmount();

    this.navigateTo('amount');
  }

  changeTitle() {
    const stepName = this.currentStep;

    this.modalTitleTarget.innerText = '';
    this.modalSubtitleTarget.innerText = '';

    if (stepName === 'amount') {
      this.modalTitleTarget.innerText = this.amountStepTitleValue;
      if (this.maxAmount()) {
        this.modalSubtitleTarget.innerText = this.creditCardAmountMaxValue;
      }
      if (this.warningMessage()) {
        this.modalSubtitleTarget.innerText = this.warningMessage();
      }
    } else if (stepName === 'paymentMethod') {
      this.modalTitleTarget.innerText = this.paymentMethodStepTitleValue;
    } else if (stepName === 'bankTransferDetails') {
      this.modalTitleTarget.innerText = this.bankTransferStepTitleValue;
      this.modalSubtitleTarget.innerText = this.bankTransferStepInfoValue;
    } else if (stepName === 'cardSelection') {
      this.modalTitleTarget.innerText = this.cardSelectionStepTitleValue;
    } else if (stepName === 'cardForm') {
      this.modalTitleTarget.innerText = this.cardFormStepTitleValue;
      this.modalSubtitleTarget.innerText = '';
    }
  }

  getCardController() {
    return this.application.getControllerForElementAndIdentifier(
      this.cardPayinContainerTarget, 'direct-card-payin'
    );
  }

  amountInputValue() {
    return Number(this.amountInputTarget.value || 0);
  }

  maxAmount() {
    if (this.paymentMethod === 'payment_method_credit_card') return this.maxAmountValue;
    return false;
  }

  warningMessage() {
    if (this.paymentMethod === 'payment_method_open_banking') return this.openBankingWarningValue;
    return false;
  }

  resetInputValue() {
    this.amountInputTarget.value = '';
    this.amountInputTarget.dispatchEvent(new Event('input', { bubbles: true }));
  }

  closeModal() {
    $(this.element).modal('hide');
  }

  _applyPrefillAmount() {
    var prefillAmount = this.element.dataset.prefillAmount;
    if (!prefillAmount) return;

    delete this.element.dataset.prefillAmount;

    this.amountFieldTarget.value = prefillAmount + ' €';
    this.amountFieldTarget.dispatchEvent(new Event('input', { bubbles: true }));
  }
}
